// src/components/TradingChart.js
import React, { useRef, useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TradingChart = ({ data, support, resistance, purchaseLevels }) => {
  const { t } = useTranslation();
  const chartContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullScreenChange = () => {
      if (document.fullscreenElement === chartContainerRef.current) {
        setIsFullscreen(true);
      } else {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  const handleToggleFullscreen = () => {
    if (!isFullscreen) {
      if (chartContainerRef.current) {
        chartContainerRef.current.requestFullscreen();
      }
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <Box
      ref={chartContainerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: 360,
      }}
    >
      <Box sx={{position: 'absolute', top: 8, left: 8, zIndex: 10}}>
        <Typography variant="caption" sx={{color: 'rgba(244,248,252,0.72)'}}>
          {t('chart.shortView')}
        </Typography>
      </Box>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
        <Button variant="contained" size="small" onClick={handleToggleFullscreen}>
          {isFullscreen ? t('chart.exitFullscreen') : t('chart.fullscreen')}
        </Button>
      </Box>

      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid stroke="rgba(126, 144, 168, 0.12)" strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" minTickGap={28} tick={{fill: '#A9B8CC', fontSize: 12}} />
          <YAxis domain={['auto', 'auto']} tick={{fill: '#A9B8CC', fontSize: 12}} />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="price"
            name={t('chart.price')}
            stroke="#58C4DD"
            strokeWidth={2.2}
            activeDot={{ r: 6 }}
            dot={false}
          />

          {support &&
            support.map((s, i) => (
              <ReferenceLine
                key={`support-${i}`}
                y={s}
                label={t('chart.support', { index: i + 1 })}
                stroke="#39D98A"
                strokeDasharray="3 3"
              />
            ))}
          {resistance &&
            resistance.map((r, i) => (
              <ReferenceLine
                key={`resistance-${i}`}
                y={r}
                label={t('chart.resistance', { index: i + 1 })}
                stroke="#FF6B6B"
                strokeDasharray="3 3"
              />
            ))}
          {purchaseLevels &&
            purchaseLevels.map((p, i) => (
              <ReferenceLine
                key={`purchase-${i}`}
                y={p}
                label={t('chart.order', { price: p })}
                stroke="#F5C36B"
                strokeDasharray="4 4"
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TradingChart;
